
import { Component, Prop } from 'vue-property-decorator';
import type AnalysisActions from '@/logic/AnalysisActions';
import type {
  Staff, Unit, Pixel, Onset,
} from '@/data';
import PixelUtils from '@/data/PixelUtils';
import type { Image3DPageData } from '@/data/Source';
import type { BResizeEmitter } from './BResizeOnArrowDrag.vue';
import type { CanvasMouseEvent } from '../LabelBehavior.vue';
import LabelBehavior from '../LabelBehavior.vue';
import { MIN_ONSETS_COVERAGE_RATE, roundX } from './utils';

interface State {
  originX: Pixel;
  originY: Pixel;
  staff: Staff;
}

/**
 * Create a new label on canvas drag
 *
 * Requires BResizeOnArrowDrag, allowing to reuse the resize part
 */
@Component
export default class BCreateOnCanvasDrag extends LabelBehavior<BResizeEmitter> {
  @Prop()
  actions!: AnalysisActions;

  @Prop()
  pixelUnit!: Unit<Pixel>;

  @Prop({ default: false })
  snap!: boolean;

  @Prop({ default: false })
  snappableWave!: boolean;

  @Prop()
  pages!: Array<Image3DPageData>;

  state: State | null = null;

  // Get the mousedown coordinates
  startCreate(event: CanvasMouseEvent, staff: Staff): void {
    this.state = { originX: event.x, originY: event.y, staff };
  }

  snapPixelToOnset(pixel: Pixel): Onset {
    // If onset coverage is enough for snapping to onsets
    const snappableWave = this.snappableWave
      && this.pixelUnit.onsetsCoverage() > MIN_ONSETS_COVERAGE_RATE;

    // Snap to onset
    if (snappableWave) {
      const onset = this.pixelUnit.toOnset(pixel, this.snap).value;
      return Math.round(onset) as Onset;
    }
    // Snap to pixel (avoids bad label pos)
    const roundedX = roundX(pixel);
    return this.pixelUnit.toOnset(roundedX, this.snap).value;
  }

  confirmCreate(): void {
    if (!this.state) return;

    const {
      staff,
      originX,
      originY,
    } = this.state;

    this.state = null;

    const onset = this.snapPixelToOnset(
      PixelUtils.getMuxPixelFromPosition(this.pages, originX, originY),
    );
    const snappedOriginX = this.pixelUnit.fromOnset(onset);

    const id = this.actions.createLabel();

    this.actions.updateLabel(id, 'onset', onset);
    this.actions.updateLabel(id, 'staffId', staff.id);

    this.actions.select(id);

    this.state = null;

    this.emit('start-resize', id, staff, snappedOriginX.value as Pixel, 0 as Pixel);
    this.$emit('drag', true);
  }

  cancelCreate(): void {
    this.state = null;
    this.$emit('drag', false);
  }

  mounted() {
    this.register('staff-mousedown', this.startCreate);
    this.register('all-mousemove', this.confirmCreate);
    this.register('document-mouseup', this.cancelCreate);
  }
}
